import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import './globals.css';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter, Route } from 'react-router-dom';
import { HandleInvalidEntityUser } from './components/HandleInvalidEntityUser';
import { Head } from './components/head/Head';
import { AppShell } from './components/layout/AppShell';
import { AppShellNoTextLoadingFallback } from './components/layout/AppShellFallback';
import { Providers } from './components/providers/Providers';
import { HomeFeatureRoute } from './components/routes/HomeFeatureRoute';
import { GameDashboardFeatureRoute } from './components/routes/InternalOnlyRoute';
import { RedirectToLanding } from './components/routes/RedirectToLanding';
import { RedirectToLocalised } from './components/routes/RedirectToLocalised';
import env from './env';
import { NotFound } from './pages/NotFound';
import { SelectDefaultGender } from './pages/settings/SelectDefaultGender';
import { VideoPlaylists } from './pages/VideoPlaylists';
import { DataLockerRoutes } from './routes/dataLockerRoutes';
import { GameRoutes } from './routes/gameRoutes';
import { PlayerRoutes } from './routes/playerRoutes';
import { RecruitmentListRoutes } from './routes/recruitmentListRoutes';
import { TeamRoutes } from './routes/teamRoutes';
import { SentryRoutes } from './utils/errorMonitoring';

const enableMocking = async () => {
  if (import.meta.env.VITE_TEST_MODE !== 'true') return;
  // Thrown here (not in MockAuthProvider) so the message reaches the screen through the
  // `Boot failed` handler below.
  const { assertLocalEndpoint } = await import('@/mocks/assertLocalEndpoint');
  assertLocalEndpoint(env.VITE_PARACHUTE_REST_API_ENDPOINT);
  // start MSW worker before React renders so every fetch is intercepted from the first render
  const { worker } = await import('@/mocks/browser');
  await worker.start({ onUnhandledRequest: 'error' });
};

const root = ReactDOM.createRoot(document.getElementById('root')!);

const renderApp = () => {
  root.render(
    <React.StrictMode>
      <HelmetProvider>
        <Head />
        <BrowserRouter>
          <Providers>
            <Suspense fallback={<AppShellNoTextLoadingFallback />}>
              <HandleInvalidEntityUser>
                <SentryRoutes>
                  <Route path="/">
                    <Route
                      path=""
                      element={
                        <AppShellNoTextLoadingFallback>
                          <RedirectToLocalised />
                        </AppShellNoTextLoadingFallback>
                      }
                    />
                    <Route path=":locale" element={<AppShell />}>
                      <Route path="" element={<RedirectToLanding />} />
                      <Route path="home" element={<HomeFeatureRoute />} />
                      <Route path="player/:playerId/*" element={<PlayerRoutes />} />
                      <Route path="team/:teamId/*" element={<TeamRoutes />} />
                      <Route
                        path="match/:gameId/*"
                        element={
                          <GameDashboardFeatureRoute>
                            <GameRoutes />
                          </GameDashboardFeatureRoute>
                        }
                      />
                      <Route path="data-locker/*" element={<DataLockerRoutes />} />
                      <Route path="video-playlists/*" element={<VideoPlaylists />} />
                      {/* Thought it'd be a nicer experience if someone does try to go via the URL that they'd get
                      redirected back, as without that they just get stuck at /en/reports */}
                      <Route path="reports" element={<RedirectToLanding />} />
                      <Route path="recruitment-list/*" element={<RecruitmentListRoutes />} />
                      <Route path="settings/default-gender" element={<SelectDefaultGender />} />
                      <Route path="*" element={<NotFound />} />
                    </Route>
                  </Route>
                </SentryRoutes>
              </HandleInvalidEntityUser>
            </Suspense>
          </Providers>
        </BrowserRouter>
      </HelmetProvider>
    </React.StrictMode>,
  );
};

enableMocking()
  .then(renderApp)
  .catch((err: unknown) => {
    document.getElementById('root')!.textContent = `Boot failed: ${err instanceof Error ? err.message : String(err)}`;
    // eslint-disable-next-line no-console
    console.error('[boot]', err);
  });
